import { useEffect, useState } from "react";
import { LabTestOrder, Pagination } from "../Orders/Types";
import { OrderService } from "../../../services/api/order.service";
import avatarPlaceholder from "../../../assets/images/default_avatar.jpg";
import { DataTable } from "../../DataTable/DataTable";
import * as XLSX from "xlsx";
import toast from "react-hot-toast";
import { useAuth } from "../../../context/AuthContext";

interface ClinicList {
  id: string;
  name: string;
  profileImage: string;
}

interface Filters {
  startDate: string;
  endDate: string;
  orderType: string;
  orderStatus: string;
  clinicId: string;
  searchBy:
  | "patientName"
  | "patientPhone"
  | "productName" // Should we keep this? Maybe "labtestName" but backend might map it generic
  | "batchNumber" // Irrelevant for labtest usually
  | "orderNumber"
  | "doctorName"
  | "";
  searchText: string;
}

const LabtestOrderReport = () => {
  const { user } = useAuth()
  const formatDate = (date: Date) => date.toISOString().split("T")[0];
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const initialStartDate = formatDate(yesterday);
  const initialEndDate = formatDate(today);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    pageSize: 10,
    totalItemsCount: 0,
    totalPages: 0,
  });

  const [data, setData] = useState<LabTestOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [clinicList, setClinicList] = useState<ClinicList[]>([]);
  const [downloading, setDownloading] = useState(false);

  const [filters, setFilters] = useState<Filters>({
    startDate: initialStartDate,
    endDate: initialEndDate,
    orderType: "",
    orderStatus: "",
    clinicId: user?.role === "clinic" ? user?.id : "",
    searchBy: "",
    searchText: "",
  });

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      startDate: initialStartDate,
      endDate: initialEndDate,
    }));
  }, []);

  const getClinicList = async () => {
    const res = await OrderService.getClinicList();
    setClinicList(res.data);
  };

  const getLabtestOrderReport = async () => {
    try {
      setLoading(true);
      const res = await OrderService.getLabtestOrderReport(
        filters,
        pagination.page,
        pagination.pageSize,
        false
      );
      // We assume res.data matches LabtestItem[] structure roughly or adapt it
      setData(res.data as unknown as LabTestOrder[]);
      setPagination(res.pagination);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getClinicList();
  }, []);

  useEffect(() => {
    getLabtestOrderReport();
  }, [filters, pagination.page, pagination.pageSize]);

  const handlePageChange = (page: number) =>
    setPagination((p) => ({ ...p, page }));

  const handlePageSizeChange = (pageSize: number) =>
    setPagination((p) => ({ ...p, pageSize, page: 1 }));

  const updateFilter = (key: keyof Filters, value: string) => {
    setPagination((p) => ({ ...p, page: 1 }));
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleExportToExcel = async () => {
    setDownloading(true);

    try {
      const res = await OrderService.getLabtestOrderReport(
        filters,
        pagination.page,
        pagination.pageSize,
        true
      );

      const data = (res?.data || []) as unknown as LabTestOrder[];

      if (!data.length) {
        toast.error("No data found");
        return;
      }

      const formattedData = data.map((item) => ({
        "Lab Test Name": item?.labTest?.name || "N/A",
        "Order Number": item?.orderNumber || "N/A",
        "Patient Name": item?.patient?.patientName || "N/A",
        "Patient Phone": item?.patient?.patientMobileNumber || "N/A",
        "Clinic Name": item?.clinic?.clinicName || "N/A",
        "Doctor Name": item?.doctorName || "N/A",

        "Order Date": item?.orderDate
          ? new Date(item.orderDate).toLocaleString("en-GB")
          : "N/A",

        // Keep prices as actual numbers
        "Original Price": Number(item?.labTest?.originalPrice) || 0,
        "Offer Price": Number(item?.labTest?.offerPrice) || 0,

        "Report Time": item?.labTest?.reportTime || "N/A",
      }));

      // Create worksheet
      const worksheet = XLSX.utils.json_to_sheet(formattedData);

      // Format numeric columns
      // H = Original Price
      // I = Offer Price
      formattedData.forEach((_, index) => {
        const row = index + 2;

        ["H", "I"].forEach((column) => {
          const cell = worksheet[`${column}${row}`];

          if (cell) {
            cell.t = "n";
            cell.z = "0.00";
          }
        });
      });

      // Create workbook
      const workbook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        "Labtest Order Report"
      );

      // Download
      XLSX.writeFile(
        workbook,
        "labtest_order_report.xlsx"
      );
    } catch (error) {
      console.error("Error exporting labtest order report:", error);
      toast.error("Error while exporting the order report");
    } finally {
      setDownloading(false);
    }
  }


  const columns = [
    {
      key: "Lab Test",
      title: "Lab Test",
      sortable: true,
      width: "w-[250px]",
      render: (item: LabTestOrder) => (
        <div className="flex items-center">
          <img
            src={item?.labTest?.icon || avatarPlaceholder}
            alt={item?.labTest?.name}
            className="w-8 h-8 rounded-full mr-3 object-cover border border-gray-200"
          />
          <div>
            <div className="font-medium text-gray-900">
              {item?.labTest?.name}
            </div>
            <div className="text-xs text-gray-500">
              {item?.labTest?.labTestCategory}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "orderNumber",
      title: "Order Number",
      render: (item: LabTestOrder) => <div>{item?.orderNumber || "N/A"}</div>,
    },
    {
      key: "Patient Name",
      title: "Patient Name",
      sortable: true,
      width: "w-[250px]",
      render: (item: LabTestOrder) => (
        <div className="flex items-center">
          <img
            src={item?.patient?.patientProfileImage || avatarPlaceholder}
            alt={item?.patient?.patientName}
            className="w-8 h-8 rounded-full mr-3 object-cover border border-gray-200"
          />
          <div>
            <div className="font-medium text-gray-900">
              {item?.patient?.patientName}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "mobileNumber",
      title: "Mobile Number",
      render: (item: LabTestOrder) => (
        <div>{item?.patient?.patientMobileNumber || "N/A"}</div>
      ),
    },
    {
      key: "orderDate",
      title: "Order Date",
      render: (item: LabTestOrder) => (
        <div>
          {new Date(item?.orderDate).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          }) || "N/A"}
        </div>
      ),
    },
    ...(user?.role !== "clinic" ? [{
      key: "Clinic Name",
      title: "Clinic Name",
      sortable: true,
      width: "w-[250px]",
      render: (item: LabTestOrder) => (
        <div className="flex items-center">
          <img
            src={item?.clinic?.clinicProfileImage || avatarPlaceholder}
            alt={item?.clinic?.clinicName}
            className="w-8 h-8 rounded-full mr-3 object-cover border border-gray-200"
          />
          <div>
            <div className="font-medium text-gray-900">
              {item?.clinic?.clinicName}
            </div>
          </div>
        </div>
      ),
    },] : []),
    {
      key: "doctorName",
      title: "Doctor Name",
      render: (item: LabTestOrder) => <div>{item?.doctorName || "N/A"}</div>,
    },
    {
      key: "Price",
      title: "Price",
      render: (item: LabTestOrder) => (
        <div className="font-medium">
          ₹{item?.labTest?.offerPrice || item?.labTest?.originalPrice}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4 mt-4">
      <div className="flex items-end justify-end"></div>
      {/* FILTERS */}
      <div className={`grid grid-cols-1 gap-4 bg-white p-4 rounded-lg border ${user?.role === "clinic" ? "md:grid-cols-6" : "md:grid-cols-7"}`}>
        {/* Search By */}
        <select
          className="border rounded px-3 py-2"
          value={filters.searchBy}
          onChange={(e) => updateFilter("searchBy", e.target.value)}
        >
          <option value="">Search By</option>
          <option value="patientName">Patient Name</option>
          <option value="patientPhone">Patient Phone</option>
          {/* <option value="productName">Product Name</option>
          <option value="batchNumber">Batch Number</option> */}
          <option value="orderNumber">Order Number</option>
          <option value="doctorName">Doctor Name</option>
        </select>

        {/* Search Text */}
        <input
          type="text"
          placeholder="Search..."
          className="border rounded px-3 py-2"
          value={filters.searchText}
          onChange={(e) => updateFilter("searchText", e.target.value)}
        />

        {/* Order Status */}
        <select
          className="border rounded px-3 py-2"
          value={filters.orderStatus}
          onChange={(e) => updateFilter("orderStatus", e.target.value)}
        >
          <option value="">Order Status</option>
          <option value="PENDING">Pending</option>
          <option value="COMPLETED">Completed</option>
          <option value="CANCELLED">Cancelled</option>
        </select>

        {/* From Date */}
        <input
          type="date"
          className="border rounded px-3 py-2"
          value={filters.startDate}
          onChange={(e) => updateFilter("startDate", e.target.value)}
        />

        {/* To Date */}
        <input
          type="date"
          className="border rounded px-3 py-2"
          value={filters.endDate}
          onChange={(e) => updateFilter("endDate", e.target.value)}
        />

        {/* Clinic */}
        {user?.role !== "clinic" && <select
          className="border rounded px-3 py-2"
          value={filters.clinicId}
          onChange={(e) => updateFilter("clinicId", e.target.value)}
        >
          <option value="">All Clinics</option>
          {clinicList.map((clinic) => (
            <option key={clinic.id} value={clinic.id}>
              {clinic.name}
            </option>
          ))}
        </select>}
        <button
          onClick={handleExportToExcel}
          className="px-3 py-2 left-1 bg-green-600 text-white rounded-md text-sm hover:bg-green-700"
          disabled={downloading}
        >
          {downloading ? "Exporting..." : "Export to Excel"}
        </button>
      </div>

      {/* TABLE */}
      <DataTable
        data={data}
        columns={columns}
        itemsPerPage={pagination.pageSize}
        isLoading={loading}
        pagination={{
          currentPage: pagination.page,
          setCurrentPage: handlePageChange,
          totalPages: pagination.totalPages,
          totalItemsCount: pagination.totalItemsCount,
          pageSize: pagination.pageSize,
          setPageSize: handlePageSizeChange,
        }}
      />
    </div>
  );
};

export default LabtestOrderReport;
